import { useEffect, useMemo, useRef } from "react";
import { debounce } from "lodash";
import { useAppSelector } from "redux/hooks";
import type { StompState } from "redux/stomp/stompSlice";
import { RootState } from "redux/store";

/** STOMP로 갱신 시각이 올 때 목록 refetch에 쓰는 `StompState` 키 */
export type StompDateSignalKey = {
  [K in keyof StompState]: StompState[K] extends Date | null ? K : never;
}[keyof StompState];
type SignalState = object;
type SignalKey<T extends SignalState> = Extract<keyof T, string>;

type RefetchFn = () => void | Promise<unknown>;

export interface UseStompDebouncedRefetchOptions {
  stompKey: StompDateSignalKey | StompDateSignalKey[];
  refetch: RefetchFn;
  enabled?: boolean;
  waitMs?: number;
}

type GenericUseStompDebouncedRefetchOptions<T extends SignalState> = Omit<
  UseStompDebouncedRefetchOptions,
  "stompKey"
> & {
  stompKey: SignalKey<T> | SignalKey<T>[] | string | string[];
  selector?: (state: RootState) => unknown;
};

const toSignalValue = (value: unknown): unknown => {
  if (
    typeof value === "object" &&
    value !== null &&
    "updatedDate" in (value as Record<string, unknown>)
  ) {
    return (value as { updatedDate?: unknown }).updatedDate;
  }
  return value;
};

const setSignal = <T extends SignalState>(
  stompState: T,
  stompKey: SignalKey<T> | SignalKey<T>[]
) => {
  const keys: SignalKey<T>[] = Array.isArray(stompKey) ? stompKey : [stompKey];
  return keys
    .map((key) => {
      const value = toSignalValue((stompState as Record<string, unknown>)[key]);
      if (value === null || value === undefined) {
        return "null";
      }
      if (value instanceof Date) {
        return String(value.getTime());
      }
      if (typeof value === "string" && value !== "null") {
        const t = Date.parse(value);
        return Number.isNaN(t) ? value : String(t);
      }
      if (typeof value === "number") {
        return String(value);
      }
      return String(value);
    })
    .join("|");
};

export function useStompDebouncedRefetch<T extends SignalState = StompState>({
  stompKey,
  refetch,
  enabled = true,
  waitMs = 3000,
  selector,
}: GenericUseStompDebouncedRefetchOptions<T>): void {
  const fingerprint = useAppSelector((state: RootState) =>
    setSignal(
      (selector
        ? (selector(state) as Record<string, unknown>)
        : (state.stomp as unknown as T)) as T,
      stompKey as SignalKey<T> | SignalKey<T>[]
    )
  );
  const refetchFingerprint = useRef<string>("");

  const debouncedRefetch = useMemo(
    () =>
      debounce(() => {
        void refetch();
      }, waitMs),
    [refetch, waitMs]
  );

  useEffect(() => {
    return () => {
      debouncedRefetch.cancel();
    };
  }, [debouncedRefetch]);

  useEffect(() => {
    if (!enabled) {
      debouncedRefetch.cancel();
      return;
    }
    if (!!fingerprint) {
      console.log("fingerprint", fingerprint);
    }
    if (
      !fingerprint ||
      !fingerprint?.split("|").some((part) => part !== "null") ||
      fingerprint === "null"
    ) {
      debouncedRefetch.cancel();
      return;
    }

    if (refetchFingerprint.current !== fingerprint) {
      refetchFingerprint.current = fingerprint;
      debouncedRefetch();
    }
  }, [fingerprint, enabled, debouncedRefetch]);
}

export default useStompDebouncedRefetch;
